import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute } from "@angular/router";
import { MembershipService } from "app/core/membership/membership.service";
import { UserService } from "app/core/user/user.service";
import { MembershipList } from "app/core/user/user.types";
import { Subject, switchMap, takeUntil, tap } from "rxjs";

@Component({
    selector     : 'membership-selector',
    standalone   : true,
    templateUrl  : './membership-selector.html',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        FormsModule,
        MatSelectModule,
        MatTooltipModule
    ]
})

export class MembershipSelectorComponent implements OnInit {
    constructor() {}

    private _membershipService = inject(MembershipService);
    private _userService = inject(UserService);
    private _route = inject(ActivatedRoute);

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    selectedMember: string | null = localStorage.getItem('membershipId');
    membershipId: MembershipList[] = [];

    ngOnInit(): void {
        // Get list of memberships for dropdown and auto-select first if none selected
        this._userService.membershipList$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: MembershipList[]) => {
                this.membershipId = response;
                
                // Auto-select first membership if list is available and no membership is currently selected
                if (response?.length > 0 && !this.selectedMember) {
                    const firstMembership = response[0];
                    this._membershipService.selectedMembershipId = firstMembership.membershipId;
                    if (firstMembership.msisdn) {
                        localStorage.setItem('msisdn', firstMembership.msisdn);
                    }
                }
            });
        
        // Listen to changes in the selectedMembershipId from the service
        this._membershipService.selectedMembershipId$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((id: string | null) => {
                this.selectedMember = id;
            });

        // Check the msisdn from the URL query parameters then update the selected member
        this._route.queryParams
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(params => {
                    const msisdn = params['msisdn'];
                    if (msisdn) {
                        // Call the new service method to get membership data by msisdn
                        return this._membershipService.getMembershipByMsisdn(msisdn);
                    }
                    
                    // If no msisdn in URL, rely on auto-select from membership list
                    return this._membershipService.membershipData$;
                }),
                tap(membershipData => {
                    // Update the selected membership ID in the shared service
                    if (membershipData?.memberId) {
                        this._membershipService.selectedMembershipId = membershipData.memberId;
                    }
                })
            )
            .subscribe();
    }

    onMembershipChange(newId: string): void {
        const selected = this.membershipId.find(m => m.membershipId === newId);

        if (selected?.msisdn) {
            localStorage.setItem('msisdn', selected.msisdn);
        }

        this._membershipService.selectedMembershipId = newId;
    }

    formattMsisdnView(phone: string): string {
        if (!phone.startsWith('60') || phone.length < 5) {
          return phone;
        }
      
        const countryCode = '+60';
        const areaCode = phone.substring(2, 4);
        const remaining = phone.substring(4);
      
        return `${countryCode}${areaCode}-${remaining}`;
    }

    getSelectedDisplay(): string {
        if (!this.selectedMember) {
            return '';
        }

        const selected = this.membershipId.find(m => m.membershipId === this.selectedMember);
        const msisdn = selected?.msisdn ? this.formattMsisdnView(selected.msisdn) : '';
        return msisdn ? `${msisdn} (${this.selectedMember})` : this.selectedMember;
    }

}
